// Ventas diarias del sistema de ventas (proyecto sistema-ventas): resumen mensual por contrato,
// estado de completitud y vínculo con los contratos de Control-Gestion.
import { normalizarRut } from "@/helpers/importarExcelFacturacion";

/** Registro diario tal como lo guarda el sistema de ventas (colección «eventos»). */
export interface EventoVenta {
  code: string;
  start: string; // YYYY-MM-DD
  amounts: unknown; // se guarda como texto
}

/** Contrato del sistema de ventas (colección «contratos»). */
export interface ContratoVentas {
  code: string;
  identifier?: string;
  shop?: string;
  tradename?: string;
  businessname?: string;
  mall?: string;
}

export type EstadoVentaMes = "completo" | "con-huecos" | "incompleto" | "sin-registro";

export interface ResumenVentaMes {
  codigoVentas: string;
  total: number;
  diasRegistrados: number;
  diasMes: number;
  ultimoDia: number; // 0 si no hay registros
  faltantes: number[]; // días del mes sin registro
  estado: EstadoVentaMes;
}

export function diasDelMes(periodo: string): number {
  const [y, m] = periodo.split("-").map(Number);
  return new Date(y, m, 0).getDate();
}

/** Rango [desde, hasta] de fechas YYYY-MM-DD del mes, para consultar los eventos. */
export function rangoDelMes(periodo: string): [string, string] {
  return [`${periodo}-01`, `${periodo}-${String(diasDelMes(periodo)).padStart(2, "0")}`];
}

/**
 * Completo: registra todos los días. Con huecos: llega al último día pero le faltan días
 * en medio (locales que no abren todos los días). Incompleto: no llega al último día.
 */
export function estadoDelMes(dias: Set<number>, periodo: string): Pick<ResumenVentaMes, "ultimoDia" | "faltantes" | "estado"> {
  const n = diasDelMes(periodo);
  const faltantes: number[] = [];
  for (let d = 1; d <= n; d++) if (!dias.has(d)) faltantes.push(d);
  const ultimoDia = dias.size ? Math.max(...Array.from(dias)) : 0;
  const estado: EstadoVentaMes = !dias.size
    ? "sin-registro"
    : ultimoDia < n
    ? "incompleto"
    : faltantes.length
    ? "con-huecos"
    : "completo";
  return { ultimoDia, faltantes, estado };
}

const aMonto = (v: unknown): number => {
  const n = Number(String(v ?? "").replace(/\s/g, ""));
  return isFinite(n) ? n : 0;
};

/** Suma las ventas diarias del mes por código del sistema de ventas. */
export function resumirVentasMes(eventos: EventoVenta[], periodo: string): Map<string, ResumenVentaMes> {
  const acumulado = new Map<string, { total: number; dias: Set<number> }>();
  for (const e of eventos) {
    if (!e.start || String(e.start).substr(0, 7) !== periodo) continue;
    const code = String(e.code);
    const a = acumulado.get(code) || { total: 0, dias: new Set<number>() };
    a.total += aMonto(e.amounts);
    a.dias.add(Number(String(e.start).substr(8, 2)));
    acumulado.set(code, a);
  }
  const resumen = new Map<string, ResumenVentaMes>();
  acumulado.forEach((a, code) =>
    resumen.set(code, {
      codigoVentas: code,
      total: Math.round(a.total),
      diasRegistrados: a.dias.size,
      diasMes: diasDelMes(periodo),
      ...estadoDelMes(a.dias, periodo),
    })
  );
  return resumen;
}

/** Resumen vacío para un contrato vinculado que no registró nada en el mes. */
export function resumenSinRegistro(codigoVentas: string, periodo: string): ResumenVentaMes {
  return { codigoVentas, total: 0, diasRegistrados: 0, diasMes: diasDelMes(periodo), ...estadoDelMes(new Set(), periodo) };
}

/** "LC 1033 D", "1033 D" → "1033D"; "ISLA NRO.14", "Isla 14" → "ISLA14". */
export function normalizarLocal(texto: string): string {
  const t = String(texto || "").toUpperCase().replace(/NRO\.?|N°|LOCAL|\bLC\b/g, "").replace(/[^A-Z0-9]/g, "");
  return t.replace(/^ISLA0+/, "ISLA");
}

export interface ContratoParaVincular {
  codigo: number | string;
  rut: string;
  local: string;
  /** Ventas ya cargadas del contrato (por ejemplo, desde el Excel): periodo → venta. */
  ventas: Record<string, number>;
}

export interface Sugerencia {
  codigoVentas: string;
  motivo: string;
}

/**
 * Sugiere el contrato del sistema de ventas que corresponde. En orden: ventas iguales a las ya
 * cargadas (lo más seguro: el RUT del sistema de ventas no siempre es el de facturación),
 * RUT + local, local único y RUT único.
 */
export function sugerirVinculo(
  contrato: ContratoParaVincular,
  contratosVentas: ContratoVentas[],
  totales: Record<string, Record<string, number>>, // codigoVentas → periodo → total
  usados: Set<string> = new Set(),
  criterio: "todos" | "ventas" | "datos" = "todos"
): Sugerencia | null {
  const libres = contratosVentas.filter((c) => !usados.has(String(c.code)));
  const periodos = Object.keys(contrato.ventas).filter((p) => contrato.ventas[p] > 0);
  if (periodos.length && criterio !== "datos") {
    const iguales = libres.filter((c) =>
      periodos.some((p) => Math.abs((totales[String(c.code)]?.[p] ?? -1e9) - contrato.ventas[p]) <= 2)
    );
    if (iguales.length === 1) return { codigoVentas: String(iguales[0].code), motivo: "ventas iguales" };
  }
  if (criterio === "ventas") return null;
  const rut = normalizarRut(contrato.rut);
  const local = normalizarLocal(contrato.local);
  const mismoRut = rut ? libres.filter((c) => normalizarRut(String(c.identifier || "")) === rut) : [];
  const mismoLocal = local ? libres.filter((c) => normalizarLocal(String(c.shop || "")) === local) : [];
  const ambos = mismoRut.filter((c) => mismoLocal.includes(c));
  if (ambos.length === 1) return { codigoVentas: String(ambos[0].code), motivo: "RUT y local" };
  if (mismoLocal.length === 1) return { codigoVentas: String(mismoLocal[0].code), motivo: "local" };
  if (mismoRut.length === 1) return { codigoVentas: String(mismoRut[0].code), motivo: "RUT" };
  return null;
}

/** Venta a facturar: la del sistema más el ajuste manual (puede ser negativo). */
export function ventaFinal(total: number, ajuste: number): number {
  return Math.round(total + (Number(ajuste) || 0));
}
